"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import styles from "./RouteLoadingIndicator.module.scss";
import {
    ROUTE_LOADING_START_EVENT,
    type RouteLoadingStartDetail,
} from "./routeLoadingEvents";
import {
    normalizeComparablePathname,
    toComparableLocationKey,
} from "./isSameRouteNavigation";

const SHOW_DELAY_MS = 180;
const MIN_VISIBLE_MS = 260;
const COMPLETE_HOLD_MS = 180;
const PROGRESS_TICK_MS = 120;
const INITIAL_PROGRESS = 0.08;
const MAX_PROGRESS = 0.9;

type TimerHandle = ReturnType<typeof setTimeout>;
type IntervalHandle = ReturnType<typeof setInterval>;
type TimerRef = { current: TimerHandle | null };
type HistoryUrl = Parameters<History["pushState"]>[2];

function getCurrentLocationKey(): string {
    if (typeof window === "undefined") {
        return "";
    }
    return toComparableLocationKey(new URL(window.location.href));
}

function resolveHistoryUrl(url: HistoryUrl, currentHref: string): URL | null {
    if (url == null) {
        return null;
    }
    try {
        return new URL(url.toString(), currentHref);
    } catch {
        return null;
    }
}

export function shouldStartForHistoryNavigation(
    url: HistoryUrl,
    currentHref: string
): boolean {
    const currentUrl = new URL(currentHref);
    const targetUrl = resolveHistoryUrl(url, currentHref);

    if (!targetUrl) {
        return false;
    }

    if (targetUrl.origin !== currentUrl.origin) {
        return false;
    }

    return (
        toComparableLocationKey(targetUrl) !==
        toComparableLocationKey(currentUrl)
    );
}

export function shouldStartForClick(event: MouseEvent): boolean {
    if (event.defaultPrevented || event.button !== 0) {
        return false;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return false;
    }

    const target = event.target as Element | null;
    const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
    if (!anchor) {
        return false;
    }

    if (anchor.hasAttribute("download")) {
        return false;
    }

    const targetAttr = anchor.getAttribute("target");
    if (targetAttr && targetAttr !== "_self") {
        return false;
    }

    const href = anchor.getAttribute("href");
    if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("javascript:")
    ) {
        return false;
    }

    let url: URL;
    try {
        url = new URL(anchor.href, window.location.href);
    } catch {
        return false;
    }

    if (url.origin !== window.location.origin) {
        return false;
    }

    return !(
        normalizeComparablePathname(url.pathname) ===
            normalizeComparablePathname(window.location.pathname) &&
        url.search === window.location.search
    );
}

export default function RouteLoadingIndicator() {
    const pathname = usePathname() ?? "";
    const searchParams = useSearchParams();
    const search = searchParams?.toString() ?? "";
    const routeKey = `${normalizeComparablePathname(pathname)}${search ? `?${search}` : ""}`;
    const previousRouteKeyRef = useRef(routeKey);
    const lastKnownLocationKeyRef = useRef(
        typeof window !== "undefined" ? getCurrentLocationKey() : ""
    );
    const isVisibleRef = useRef(false);
    const navigationStartedRef = useRef(false);
    const visibleSinceRef = useRef(0);
    const showTimerRef = useRef<TimerHandle | null>(null);
    const completeTimerRef = useRef<TimerHandle | null>(null);
    const hideTimerRef = useRef<TimerHandle | null>(null);
    const progressIntervalRef = useRef<IntervalHandle | null>(null);

    const [isVisible, setIsVisible] = useState(false);
    const [progress, setProgress] = useState(0);

    const stopProgressInterval = useCallback(() => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
    }, []);

    const clearTimer = useCallback((ref: TimerRef) => {
        if (ref.current) {
            clearTimeout(ref.current);
            ref.current = null;
        }
    }, []);

    const activateIndicator = useCallback(() => {
        stopProgressInterval();
        setIsVisible(true);
        visibleSinceRef.current = Date.now();
        setProgress(INITIAL_PROGRESS);

        progressIntervalRef.current = setInterval(() => {
            setProgress((current) => {
                if (current >= MAX_PROGRESS) {
                    stopProgressInterval();
                    return current;
                }
                const remaining = MAX_PROGRESS - current;
                const step = Math.max(0.01, remaining * 0.18);
                return Math.min(MAX_PROGRESS, current + step);
            });
        }, PROGRESS_TICK_MS);
    }, [stopProgressInterval]);

    const finishNavigation = useCallback(() => {
        if (!navigationStartedRef.current && !isVisibleRef.current) {
            return;
        }

        navigationStartedRef.current = false;
        clearTimer(showTimerRef);
        stopProgressInterval();

        if (!isVisibleRef.current) {
            setProgress(0);
            return;
        }

        const elapsed = Date.now() - visibleSinceRef.current;
        const waitForMinVisible = Math.max(0, MIN_VISIBLE_MS - elapsed);

        clearTimer(completeTimerRef);
        clearTimer(hideTimerRef);

        completeTimerRef.current = setTimeout(() => {
            setProgress(1);
            hideTimerRef.current = setTimeout(() => {
                setIsVisible(false);
                setProgress(0);
            }, COMPLETE_HOLD_MS);
        }, waitForMinVisible);
    }, [clearTimer, stopProgressInterval]);

    const startNavigation = useCallback(
        (showDelayMs: number = SHOW_DELAY_MS) => {
            if (navigationStartedRef.current) {
                return;
            }

            navigationStartedRef.current = true;
            clearTimer(completeTimerRef);
            clearTimer(hideTimerRef);
            clearTimer(showTimerRef);
            stopProgressInterval();

            if (showDelayMs <= 0) {
                activateIndicator();
                return;
            }

            showTimerRef.current = setTimeout(() => {
                activateIndicator();
            }, showDelayMs);
        },
        [activateIndicator, clearTimer, stopProgressInterval]
    );

    useEffect(() => {
        isVisibleRef.current = isVisible;
    }, [isVisible]);

    useEffect(() => {
        if (previousRouteKeyRef.current === routeKey) {
            return;
        }

        previousRouteKeyRef.current = routeKey;
        lastKnownLocationKeyRef.current = getCurrentLocationKey();
        finishNavigation();
    }, [finishNavigation, routeKey]);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (shouldStartForClick(event)) {
                startNavigation();
            }
        };

        document.addEventListener("click", handleClick, true);
        return () => {
            document.removeEventListener("click", handleClick, true);
        };
    }, [startNavigation]);

    useEffect(() => {
        const handleManualStart = (event: Event) => {
            const detail = (event as CustomEvent<RouteLoadingStartDetail>)
                .detail;
            startNavigation(detail?.immediate ? 0 : SHOW_DELAY_MS);
        };

        window.addEventListener(
            ROUTE_LOADING_START_EVENT,
            handleManualStart as EventListener
        );
        return () => {
            window.removeEventListener(
                ROUTE_LOADING_START_EVENT,
                handleManualStart as EventListener
            );
        };
    }, [startNavigation]);

    useEffect(() => {
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        const patchedPushState: History["pushState"] = (data, unused, url) => {
            const currentHref = window.location.href;
            const shouldStart = shouldStartForHistoryNavigation(
                url,
                currentHref
            );
            originalPushState.call(window.history, data, unused, url);
            lastKnownLocationKeyRef.current = getCurrentLocationKey();
            if (shouldStart) {
                startNavigation();
            }
        };

        const patchedReplaceState: History["replaceState"] = (
            data,
            unused,
            url
        ) => {
            const currentHref = window.location.href;
            const shouldStart = shouldStartForHistoryNavigation(
                url,
                currentHref
            );
            originalReplaceState.call(window.history, data, unused, url);
            lastKnownLocationKeyRef.current = getCurrentLocationKey();
            if (shouldStart) {
                startNavigation();
            }
        };

        const handlePopState = () => {
            const nextLocationKey = getCurrentLocationKey();
            if (nextLocationKey !== lastKnownLocationKeyRef.current) {
                lastKnownLocationKeyRef.current = nextLocationKey;
                startNavigation();
            }
        };

        window.history.pushState = patchedPushState;
        window.history.replaceState = patchedReplaceState;
        window.addEventListener("popstate", handlePopState);

        return () => {
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
            window.removeEventListener("popstate", handlePopState);
        };
    }, [startNavigation]);

    useEffect(() => {
        return () => {
            clearTimer(showTimerRef);
            clearTimer(completeTimerRef);
            clearTimer(hideTimerRef);
            stopProgressInterval();
        };
    }, [clearTimer, stopProgressInterval]);

    return (
        <div
            className={`${styles.container} ${isVisible ? styles.visible : ""}`}
            aria-hidden="true"
            data-testid="route-loading-indicator"
        >
            <div
                className={styles.bar}
                style={{ transform: `scaleX(${progress})` }}
            />
        </div>
    );
}
